import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Lock, ShieldAlert } from 'lucide-react'
import { config } from '@/config/runtime-config'
import { api } from '@/shared/api/client'
import { explain } from '@/shared/api/problem'
import { useSession } from '@/shared/auth/session'
import { getAccessToken } from '@/shared/auth/tokenStore'
import { hydrateSession } from '@/shared/auth/AuthGate'

export function ForcedPasswordChangePage() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const patchUser = useSession((s) => s.patchUser)

  const mismatch = confirm.length > 0 && next !== confirm

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (mismatch) return
    setBusy(true); setError('')
    try {
      await api.post('/auth/password/change', { currentPassword: current, newPassword: next })
      patchUser({ mustChangePassword: false })
      const token = getAccessToken()
      if (token) await hydrateSession(token)
      navigate('/', { replace: true })
    } catch (err) {
      setError(explain(err))
    } finally { setBusy(false) }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="erp-card p-6">
          <div className="flex items-start gap-2.5 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-md">
            <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-erp-sm text-amber-900">
              <b>Change your password to continue.</b>
              <div className="mt-0.5 text-amber-800">
                This account is still using the password it was created with. Every other request is
                blocked until it changes.
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {[
              { id: 'cur', label: 'Current password', v: current, set: setCurrent, ac: 'current-password' },
              { id: 'new', label: 'New password', v: next, set: setNext, ac: 'new-password' },
              { id: 'cf', label: 'Confirm new password', v: confirm, set: setConfirm, ac: 'new-password' },
            ].map((f) => (
              <div key={f.id}>
                <label htmlFor={f.id} className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">{f.label}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
                  <input
                    id={f.id} type="password" required autoComplete={f.ac}
                    value={f.v} onChange={(e) => f.set(e.target.value)}
                    className="w-full h-11 pl-10 pr-3 text-erp-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                  />
                </div>
              </div>
            ))}
            {mismatch && <div className="text-erp-sm text-red-700">Passwords do not match.</div>}
            {error && (
              <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-md text-erp-sm text-rose-700">{error}</div>
            )}
            <button
              type="submit" disabled={busy || mismatch || !current || !confirm}
              className="w-full h-11 inline-flex items-center justify-center text-erp-sm bg-chocolate text-chocolate-foreground rounded-md hover:bg-chocolate-muted disabled:opacity-50 font-medium transition-colors"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Change password and continue
            </button>
            <p className="text-erp-xs text-muted-foreground m-0">
              Changing your password signs out every other session on this account.
            </p>
          </form>
        </div>
        <p className="mt-3 text-center text-erp-xs text-muted-foreground">
          {config.appName} · {config.release}
        </p>
      </div>
    </div>
  )
}
